import { useState } from "react";
import { UncontrolledPopover, PopoverBody } from "reactstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { formatDate } from "../../utils/formatDate";
import { getFlagMeta } from "../../utils/statusMeta";
import { StatusChip } from "../common/StatusChip";

const CopyCell = ({ value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Error al copiar:", err);
    }
  };

  return (
    <span
      style={{
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
      onClick={handleCopy}
      title="Copiar"
    >
      {value}
      <i className={copied ? "ri-check-line" : "ri-file-copy-line"} style={{ color: copied ? "#0F9A5A" : "#9AA1BC", fontSize: 13 }} />
    </span>
  );
};

const columnHelper = createColumnHelper();

export const getUserColumns = (
  navigate,
  onEditClick,
  onActivateNetwork,
  onBonusClick,
  onRedAscendente,
  onRangoDistribuidorClick,
  admin
) => {
  const isAdmin = admin && ['ADMIN'].includes(admin?.role);

  const columns = [
    columnHelper.accessor("multyhealth_code", {
      header: "Código",
      cell: ({ getValue }) => <CopyCell value={getValue()} />,
    }),

    columnHelper.accessor("name", {
      header: "Nombre",
      cell: ({ getValue }) => <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{getValue()}</span>,
    }),

    columnHelper.accessor("document", {
      header: "Documento",
      cell: ({ getValue }) => (
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#5A6187" }}>
          {getValue() || "-"}
        </span>
      ),
    }),

    columnHelper.accessor("email", {
      header: "Correo",
      cell: ({ getValue }) => <span style={{ color: "#5A6187", fontSize: 12.5 }}>{getValue()}</span>,
    }),

    columnHelper.accessor((row) => {
      const ref = row?.reference_id;
      return ref ? `${ref.multyhealth_code} - ${ref.name}` : "-";
    }, {
      id: "sponsor_full",
      header: "Auspiciador",
      cell: ({ getValue }) => <span style={{ color: "#5A6187", fontSize: 12.5, whiteSpace: "nowrap" }}>{getValue()}</span>,
    }),

    columnHelper.accessor((row) => row?.network?.influencer_bonus_status, {
      id: "bonus_status",
      header: "Estado Bono",
      cell: ({ getValue }) => {
        const meta = getFlagMeta(getValue());
        return <StatusChip tone={meta.tone}>{meta.label}</StatusChip>;
      },
    }),

    columnHelper.accessor((row) => row?.network?.loyalty_status, {
      id: "royalty_status",
      header: "Estado Regalía",
      cell: ({ getValue }) => {
        const meta = getFlagMeta(getValue());
        return <StatusChip tone={meta.tone}>{meta.label}</StatusChip>;
      },
    }),

    columnHelper.accessor((row) => row?.network?.activate_date, {
      id: "activate_date",
      header: "Fecha Activación",
      cell: ({ getValue }) => (
        <span style={{ color: "#5A6187", fontSize: 12.5, whiteSpace: "nowrap" }}>
          {getValue() ? formatDate(getValue()) : "No tiene"}
        </span>
      ),
    }),

    columnHelper.display({
      id: "estado",
      header: "Autenticado",
      cell: ({ row }) => {
        const state = row.original.is_confirmed;
        return (
          <StatusChip tone={state ? "success" : "danger"} icon={state ? "ri-checkbox-circle-line" : "ri-close-circle-line"}>
            {state ? "Sí" : "No"}
          </StatusChip>
        );
      },
    }),

    columnHelper.accessor("country_id.name", {
      header: "País",
      cell: ({ getValue }) => <span style={{ color: "#5A6187", fontSize: 12.5 }}>{getValue()}</span>,
    }),

    columnHelper.accessor("createdAt", {
      header: "Fecha Registro",
      cell: ({ getValue }) => {
        const raw = getValue();
        return <span style={{ color: "#5A6187", fontSize: 12.5, whiteSpace: "nowrap" }}>{raw ? formatDate(raw) : "-"}</span>;
      },
    }),
  ];

  if (isAdmin) {
    columns.push(
      columnHelper.display({
        id: "activar_red",
        header: "Activar Red",
        cell: ({ row }) => {
          const user = row.original;
          const canActivate =
            !user?.network?.influencer_bonus_status &&
            !user?.network?.loyalty_status;

          return (
            <button
              type="button"
              className="ui-activate-btn"
              disabled={!canActivate}
              onClick={() => onActivateNetwork(user)}
            >
              Activar
            </button>
          );
        },
      })
    );
  }

  columns.push(
    columnHelper.display({
      id: "acciones",
      header: "Acciones",
      cell: ({ row }) => {
        const userId = row.original.id;
        const user = row.original;
        const popoverId = `user-menu-${userId}`;

        return (
          <div className="d-flex justify-content-end">
            <button type="button" id={popoverId} className="ui-icon-btn">
              <i className="ri-more-2-fill" />
            </button>
            <UncontrolledPopover trigger="legacy" placement="bottom-end" target={popoverId}>
              <PopoverBody className="ui-menu">
                <button type="button" className="ui-menu__item" onClick={() => navigate(`/user/detail/${userId}`)}>
                  <i className="ri-eye-line" /> Ver Detalle
                </button>
                <button type="button" className="ui-menu__item" onClick={() => navigate(`/user/referral-network/${userId}`)}>
                  <i className="ri-node-tree" /> Red
                </button>
                <button type="button" className="ui-menu__item" onClick={() => onRedAscendente(user)}>
                  <i className="ri-user-shared-line" /> Red ascendente
                </button>
                <button type="button" className="ui-menu__item" onClick={() => onEditClick(user)}>
                  <i className="ri-edit-line" /> Editar
                </button>
                <button type="button" className="ui-menu__item" onClick={() => onBonusClick(user)}>
                  <i className="ri-gift-line" /> Bonificaciones
                </button>
                <button type="button" className="ui-menu__item" onClick={() => onRangoDistribuidorClick(user)}>
                  <i className="ri-medal-line" /> Rango por distribuidor
                </button>
              </PopoverBody>
            </UncontrolledPopover>
          </div>
        );
      },
    })
  );

  return columns;
};
